import { useFormContext, useFieldArray } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { EquityBridgeFormValues } from '../EquityBridgeContent';
import { isDefaultColumn, ShiftDirection } from './equityBridgeTableHelper';

export const useEquityBridgeTable = (onChange: () => void) => {
  const { getValues, setValue, control } = useFormContext<EquityBridgeFormValues>();
  const { fields: columns, move } = useFieldArray({
    control,
    name: 'columns',
    keyName: '_fieldId',
  });

  const commonNetDebtItemIds = getValues('commonNetDebtItemIds');
  const commonNetDebtItems = getValues('netDebtItems')
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => commonNetDebtItemIds.includes(item.netDebtItemId));

  const addCommonDebtItem = () => {
    const itemId = uuidv4();
    const nextItems = [...getValues('netDebtItems'), { netDebtItemId: itemId, narrative: '' }];
    setValue('netDebtItems', nextItems);
    setValue('commonNetDebtItemIds', [...getValues('commonNetDebtItemIds'), itemId]);

    getValues('columns').forEach((_column, columnIndex) =>
      setValue(`columns.${columnIndex}.netDebtValues.${itemId}`, null)
    );

    onChange();
  };

  const shiftColumn = (columnId: string, direction: ShiftDirection) => () => {
    const currentColumns = getValues('columns');
    const index = currentColumns.findIndex((column) => column.columnId === columnId);
    const targetIndex = index + (direction === ShiftDirection.Left ? -1 : 1);

    if (
      index === -1 ||
      targetIndex < 0 ||
      targetIndex >= currentColumns.length ||
      isDefaultColumn(currentColumns[targetIndex])
    ) {
      return;
    }

    move(index, targetIndex);
    onChange();
  };

  const removeCommonDebtItem = (itemId: string) => () => {
    setValue(
      'netDebtItems',
      getValues('netDebtItems').filter((item) => item.netDebtItemId !== itemId)
    );

    setValue(
      'commonNetDebtItemIds',
      getValues('commonNetDebtItemIds').filter((id) => id !== itemId)
    );

    getValues('columns').forEach((column, columnIndex) => {
      const remaining = { ...(column.netDebtValues ?? {}) };
      delete remaining[itemId];
      setValue(`columns.${columnIndex}.netDebtValues`, remaining);
    });

    onChange();
  };

  const removeColumn = (columnId: string) => () => {
    setValue(
      'columns',
      getValues('columns').filter((column) => column.columnId !== columnId)
    );
    onChange();
  };

  const canShift = (index: number, direction: ShiftDirection) => {
    const targetIndex = index + (direction === ShiftDirection.Left ? -1 : 1);
    if (targetIndex < 0 || targetIndex >= columns.length) {
      return false;
    }
    return !isDefaultColumn(columns[targetIndex]);
  };

  return {
    columns,
    commonNetDebtItems,
    addCommonDebtItem,
    shiftColumn,
    removeCommonDebtItem,
    removeColumn,
    canShift,
    isDefaultColumn,
  };
};
